'use client';

/**
 * Phone stand-in for the app-promo gallery tile. Desktop keeps the QR image;
 * on a phone the QR is no use (the visitor is already holding the phone), so
 * the tile becomes a tap-to-install card that fills exactly the same box —
 * nothing around it moves.
 */
import React from 'react';
import { withBase } from '../lib/brand';
import { useInstallLink } from './AppInstallLink';

const APP_ICON_URL = withBase('/apple-touch-icon.png');

export default function AppInstallCard({
  name,
  compact = false,
}: {
  /** Campsite display name; the card names it so the ask is specific. */
  name: string;
  /** Grid-thumbnail size: icon + button only. */
  compact?: boolean;
}) {
  const install = useInstallLink('gallery_card');
  const cta = install.opensApp ? 'Open in the app' : 'Get the free app';

  return (
    // px-14 keeps the copy clear of the gallery's prev/next arrows.
    <div
      className={`absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-b from-slate-800 to-slate-900 text-center text-white ${
        compact ? 'px-2' : 'px-14'
      }`}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={APP_ICON_URL}
        alt=""
        width={compact ? 40 : 56}
        height={compact ? 40 : 56}
        className={compact ? 'h-10 w-10 rounded-xl' : 'h-14 w-14 rounded-2xl'}
      />
      {!compact && (
        <div className="leading-snug">
          {/* text-white set on the <p> itself: the detail page styles bare <p>. */}
          <p className="text-lg font-bold text-white">Save {name} to a trip</p>
          <p className="mt-1 text-sm text-white/75">
            Free Camping Australia app · 5,000+ campsites · offline maps
          </p>
        </div>
      )}
      <a
        href={install.href}
        rel="noopener"
        onClick={(e) => {
          // The hero opens the lightbox on tap; this tap is for the store.
          e.stopPropagation();
          install.onClick();
        }}
        className={`inline-flex items-center justify-center rounded-full bg-white font-semibold text-[#1558b0] active:scale-[0.98] ${
          // Fixed width: the label settles ("Get…" → "Open…") after mount.
          compact ? 'px-3 py-1.5 text-xs' : 'w-[13rem] py-3 text-base'
        }`}
      >
        {compact ? 'Get the app' : cta}
      </a>
    </div>
  );
}
